/**
 * Projects kept in this browser.
 *
 * IndexedDB rather than sessionStorage because the source plan is part of a project and a
 * sheet is tens of megabytes - a File stored as a Blob costs nothing to keep and means reopening
 * a project does not ask for the same PDF again. It is per browser and per machine, which is
 * the trade made for not needing a server route: a project opened on another computer is not
 * there.
 *
 * The plan lives in its own store, written once. The project record is rewritten on every
 * autosave, and carrying a 40 MB Blob in it would write 40 MB for each polygon point.
 *
 * Every call degrades to a no-op. Private windows and blocked site data both make IndexedDB
 * throw, and losing the ability to save is a worse experience, not a broken one.
 */

import type { ConversionState } from "./state.ts";
import { projectName, snapshotState, type ProjectSummary } from "./project-snapshot.ts";

const DATABASE = "plan-forge";
const PROJECTS = "projects";
const FILES = "files";
const VERSION = 2;

interface StoredProject extends ProjectSummary {
  readonly contentType: string;
  readonly state: ConversionState;
  /** Only on records written before the plan moved to its own store. */
  readonly file?: Blob;
}

export interface LoadedProject extends ProjectSummary {
  readonly file: File;
  readonly state: unknown;
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE, VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(PROJECTS)) db.createObjectStore(PROJECTS, { keyPath: "id" });
      if (!db.objectStoreNames.contains(FILES)) db.createObjectStore(FILES);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/** Runs `work` against the named stores in one transaction and resolves with its result. */
async function transact<T>(
  stores: readonly string[],
  mode: IDBTransactionMode,
  work: (tx: IDBTransaction) => IDBRequest<T>,
): Promise<T> {
  const db = await open();
  return new Promise<T>((resolve, reject) => {
    const transaction = db.transaction([...stores], mode);
    const request = work(transaction);
    transaction.oncomplete = () => {
      db.close();
      resolve(request.result);
    };
    transaction.onerror = () => {
      db.close();
      reject(transaction.error);
    };
  });
}

/**
 * Saves the project record, and the plan file when `withFile` is set.
 *
 * Returns whether it was stored, so the caller knows when the file has been written and does
 * not have to write it again.
 */
export async function saveProject(
  id: string,
  file: File,
  state: ConversionState,
  withFile: boolean,
): Promise<boolean> {
  try {
    const record: StoredProject = {
      id,
      name: projectName(state, file.name),
      fileName: file.name,
      updatedAt: new Date().toISOString(),
      contentType: file.type,
      state: snapshotState(state),
    };
    await transact([PROJECTS, FILES], "readwrite", (tx) => {
      if (withFile) tx.objectStore(FILES).put(file, id);
      return tx.objectStore(PROJECTS).put(record);
    });
    return true;
  } catch {
    // See the header: not being able to save must not break the conversion.
    return false;
  }
}

export async function listProjects(): Promise<readonly ProjectSummary[]> {
  try {
    const all = await transact<StoredProject[]>([PROJECTS], "readonly", (tx) => tx.objectStore(PROJECTS).getAll());
    return all
      .map(({ id, name, fileName, updatedAt }) => ({ id, name, fileName, updatedAt }))
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  } catch {
    return [];
  }
}

export async function loadProject(id: string): Promise<LoadedProject | null> {
  try {
    const record = await transact<StoredProject | undefined>([PROJECTS], "readonly", (tx) => tx.objectStore(PROJECTS).get(id));
    const blob = await transact<Blob | undefined>([FILES], "readonly", (tx) => tx.objectStore(FILES).get(id));
    const plan = blob ?? record?.file;
    if (record === undefined || plan === undefined) return null;
    return {
      id: record.id,
      name: record.name,
      fileName: record.fileName,
      updatedAt: record.updatedAt,
      file: new File([plan], record.fileName, { type: record.contentType }),
      state: record.state,
    };
  } catch {
    return null;
  }
}

export async function deleteProject(id: string): Promise<void> {
  try {
    await transact([PROJECTS, FILES], "readwrite", (tx) => {
      tx.objectStore(FILES).delete(id);
      return tx.objectStore(PROJECTS).delete(id);
    });
  } catch {
    // As above.
  }
}
